import CloseIcon from '@mui/icons-material/Close';
import { styled, useTheme } from '@mui/material/styles';
import { FocusTrap } from '@mui/base/FocusTrap';
import { useId } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../atoms/Icon/Icon';
import { useStackedModalZIndex } from '../ConfirmationDialog/ConfirmationDialog';

const Background = styled('div')(({ theme }) => ({
    top: 0,
    height: '100%',
    width: '100%',
    position: 'fixed',
    background: 'rgba(191,191,191,0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: theme.zIndex.tooltip + 1
}));

const Card = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: '40px',
    background: theme.palette.neutral.white,
    position: 'absolute',
    maxWidth: '525px',
    minWidth: '425px'
}));
const CardHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    verticalAlign: 'baseline',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    '> svg': {
        width: theme.spacing(3.5),
        height: theme.spacing(3.5)
    }
}));

const CloseButton = styled('button')(({ theme }) => ({
    width: theme.spacing(6),
    height: theme.spacing(6),
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    position: 'absolute',
    top: theme.spacing(0.5),
    right: theme.spacing(0.5)
}));

const Title = styled('p')(({ theme }) => ({
    all: 'unset',
    color: theme.palette.neutral.black,
    fontFamily: 'TT Commons Pro',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 600,
    marginLeft: '8px'
}));

const Content = styled('p')(({ theme }) => ({
    all: 'unset',
    color: theme.palette.grey[500],
    fontFamily: 'TT Commons Pro',
    fontSize: theme.spacing(2),
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: theme.spacing(2.5),
    letterSpacing: '0.16px',
    marginBottom: '18px'
}));

const ButtonsContainer = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '29px',
    justifyContent: 'flex-end'
}));

const Button = styled('button')<{ color: string }>(({ color, theme }) => ({
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    color,
    fontFamily: 'TT Commons Pro',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '18px',
    textDecorationLine: 'underline',
    padding: 0,
    margin: 0,
    textUnderlineOffset: theme.spacing(0.5)
}));

type AlertType = 'default' | 'confirmation';

export type AlertDialogProps = {
    open: boolean;
    content: string;
    title: string;
    handleClose: () => void;
    type?: AlertType;
    icon?: string;
} & (ConfirmationType | DefaultType);

interface ConfirmationType {
    confirmButtonLabel?: string;
    cancelButtonLabel?: string;
    onCancellation?: () => void;
    onConfirmation?: () => void;
}

interface DefaultType {
    width?: string;
}

export const AlertDialog = ({
    content,
    handleClose,
    open = false,
    title,
    type = 'default',
    icon = 'warning',
    confirmButtonLabel = 'Confirm',
    cancelButtonLabel = 'Cancel',
    onConfirmation,
    onCancellation
}: AlertDialogProps & ConfirmationType) => {
    const theme = useTheme();
    const zIndex = useStackedModalZIndex(open);
    const titleId = useId();
    const contentId = useId();

    const iconFillByType: Record<string, string> = {
        warning: theme.palette.orange[300],
        logout: theme.palette.primary[500],
        default: theme.palette.primary[500]
    };

    if (!open) {
        return null;
    }

    const dialog = (
        <FocusTrap open>
            <Background style={{ zIndex }} tabIndex={-1}>
                <Card
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={title ? titleId : undefined}
                    aria-describedby={contentId}
                >
                    {type === 'default' && (
                        <CloseButton type="button" onClick={handleClose}>
                            <CloseIcon color="inherit" />
                        </CloseButton>
                    )}
                    <CardHeader>
                        <Icon id={icon} fill={iconFillByType[icon] ?? iconFillByType.default} />
                        {!!title && <Title id={titleId}>{title}</Title>}
                    </CardHeader>

                    <Content id={contentId}>{content}</Content>

                    {type === 'confirmation' && (
                        <ButtonsContainer>
                            <Button
                                type="button"
                                color={theme.palette.neutral[400]}
                                onClick={onCancellation}
                                data-testid="timeout-signout-button"
                            >
                                {cancelButtonLabel}
                            </Button>

                            <Button
                                type="button"
                                color={theme.palette.primary[500]}
                                onClick={onConfirmation}
                                data-testid="stay-signin-button"
                            >
                                {confirmButtonLabel}
                            </Button>
                        </ButtonsContainer>
                    )}
                </Card>
            </Background>
        </FocusTrap>
    );
    return createPortal(dialog, document.body);
};
